<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { condaApi } from '../condaApi';

// Assistant name from Personality (falls back to "the assistant" until it loads).
const botName = ref('the assistant');
condaApi.getSettings().then(s => { if (s?.assistantName) botName.value = s.assistantName; }).catch(() => {});

const docs = ref([]);
const url = ref('');
const busy = ref(false);
const err = ref('');
const fileInput = ref(null);
let timer = null;

async function load() { docs.value = await condaApi.listDocuments(); }
onMounted(() => { load(); timer = setInterval(load, 4000); });
onUnmounted(() => clearInterval(timer));

async function addUrl() {
  if (!/^https?:\/\//i.test(url.value)) { err.value = 'Enter a valid http(s) URL'; return; }
  busy.value = true; err.value = '';
  try { await condaApi.addUrl(url.value); url.value = ''; load(); } catch (e) { err.value = e.message; }
  busy.value = false;
}
async function upload(ev) {
  const file = ev.target.files?.[0]; if (!file) return;
  busy.value = true; err.value = '';
  try { await condaApi.uploadPdf(file); load(); } catch (e) { err.value = e.message; }
  busy.value = false;
  if (fileInput.value) fileInput.value.value = '';
}
async function reingest(id) { try { await condaApi.reingest(id); } catch (e) { err.value = e.message; } load(); }
async function del(id) { if (confirm('Delete this document? The assistant will no longer use it.')) { await condaApi.deleteDocument(id); load(); } }
const statusClass = s => (s === 'ready' ? 'green' : s === 'error' ? 'red' : 'amber');
const statusLabel = s => (s === 'ready' ? 'Ready' : s === 'error' ? "Couldn't read" : 'Reading…');
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <div class="row">
        <h1>Documents</h1>
        <span class="muted" style="font-size:12px">Web pages and PDFs {{ botName }} can answer from.</span>
      </div>
    </div>
    <div v-if="err" class="banner err">{{ err }}</div>

    <div class="card" style="margin-bottom:16px">
      <div class="row">
        <input v-model="url" class="in" placeholder="https://example.com/help/article" style="flex:1" />
        <button class="btn primary" :disabled="busy" @click="addUrl">Add web page</button>
        <span class="muted">or</span>
        <input ref="fileInput" type="file" accept="application/pdf" style="display:none" @change="upload" />
        <button class="btn" :disabled="busy" @click="fileInput && fileInput.click()">{{ busy ? 'Uploading…' : 'Upload PDF' }}</button>
      </div>
      <div class="muted sm">Add a web page link or upload a PDF. New documents are ready to use in a few moments.</div>
    </div>

    <div class="list">
      <div v-for="d in docs" :key="d.id" class="item" style="cursor:default">
        <div class="row between">
          <div class="row"><span class="pill">{{ d.source_type.toUpperCase() }}</span><strong>{{ d.title }}</strong></div>
          <span class="tag" :class="statusClass(d.status)">{{ statusLabel(d.status) }}</span>
        </div>
        <div class="muted sm">{{ d.source_ref }}</div>
        <div class="row between" style="margin-top:8px">
          <span class="muted">{{ d.char_count.toLocaleString() }} characters</span>
          <div class="row">
            <button v-if="d.source_type === 'url'" class="btn" @click="reingest(d.id)">Refresh</button>
            <button class="btn danger" @click="del(d.id)">Delete</button>
          </div>
        </div>
        <div v-if="d.error" class="muted sm" style="color:#f6a6a6">⚠ {{ d.error }}</div>
      </div>
      <div v-if="!docs.length" class="empty">No documents yet — add a web page link or upload a PDF above</div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>
